/** API client for /api/v1/brokerage/* endpoints.
 *
 * All brokerage endpoints return `{ data: T, meta: {...}, error: null }`.
 * The `request()` helper unwraps the envelope and returns `T` directly.
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

// ─── TypeScript interfaces matching backend models ────────────────────────

/** Matches backend AccountBalance Pydantic model */
export interface AccountData {
  available_cash: number;
  currency: string;
  account_id_masked: string;
}

/** Matches backend Position Pydantic model */
export interface Position {
  symbol: string;
  quantity: number;
  average_cost: number;
  current_value: number;
}

export interface PlaceOrderRequest {
  symbol: string;
  side: 'buy' | 'sell';
  order_type: 'market' | 'limit';
  quantity: number;
  limit_price?: number | null;
}

/** Matches backend OrderResult / order row returned by router */
export interface Order {
  order_id: string;
  symbol: string;
  side: 'buy' | 'sell';
  order_type: 'market' | 'limit';
  quantity: number;
  limit_price?: number | null;
  status: string;
  brokerage_order_ref?: string | null;
  submitted_at?: string | null;
}

export interface OrdersPage {
  items: Order[];
  total: number;
  page: number;
  per_page: number;
}

/** Matches backend deposit details response */
export interface DepositDetails {
  brokerage_name: string;
  bank_name: string;
  account_number: string;
  branch_code: string;
  reference_format: string;
  reference_example: string;
  instructions: string;
}

export interface WithdrawalRequest {
  amount: number;
  currency?: string;
  destination_bank_account?: string;
}

/** Matches backend WithdrawalResult model */
export interface WithdrawalResponse {
  reference: string;
  status: string;
  estimated_arrival: string;
}

/** Matches backend link response */
export interface LinkAccountResponse {
  id: string;
  brokerage_name: string;
  account_id: string;
  is_active: boolean;
  linked_at?: string | null;
}

// ─── Error type for brokerage errors ──────────────────────────────────────

export interface BrokerageError extends Error {
  status: number;
  response: {
    status: number;
    data?: {
      error?: {
        code?: string;
        message?: string;
      };
      detail?: string;
    };
  };
}

// ─── Internal envelope type ────────────────────────────────────────────────

interface ApiEnvelope<T> {
  data: T;
  meta: Record<string, unknown>;
  error: null | { code: string; message: string };
}

// ─── HTTP helper (unwraps {data, meta, error} envelope) ───────────────────

function getToken(): string | null {
  return localStorage.getItem('token');
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  if (options.headers) {
    Object.assign(headers, options.headers as Record<string, string>);
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    localStorage.removeItem('token');
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  const json = await response.json().catch(() => ({ detail: 'Request failed' }));

  if (!response.ok) {
    const err = new Error(
      json.detail || json.error?.message || 'Request failed'
    ) as BrokerageError;
    err.status = response.status;
    err.response = { status: response.status, data: json };
    throw err;
  }

  // Unwrap the {data, meta, error} envelope when present
  if (json && typeof json === 'object' && 'data' in json) {
    return (json as ApiEnvelope<T>).data;
  }

  return json as T;
}

// ─── Brokerage API functions ───────────────────────────────────────────────

export function getAccount(): Promise<AccountData> {
  return request<AccountData>('/api/v1/brokerage/account');
}

export function getPositions(): Promise<Position[]> {
  return request<Position[]>('/api/v1/brokerage/account/positions');
}

export function placeOrder(order: PlaceOrderRequest, idempotencyKey: string): Promise<Order> {
  return request<Order>('/api/v1/brokerage/orders', {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(order),
  });
}

export function getOrders(page = 1, perPage = 20): Promise<Order[]> {
  return request<Order[]>(`/api/v1/brokerage/orders?page=${page}&per_page=${perPage}`);
}

export function getOrder(orderId: string): Promise<Order> {
  return request<Order>(`/api/v1/brokerage/orders/${orderId}`);
}

export function getDepositDetails(): Promise<DepositDetails> {
  return request<DepositDetails>('/api/v1/brokerage/deposit/details');
}

export function requestWithdrawal(req: WithdrawalRequest): Promise<WithdrawalResponse> {
  return request<WithdrawalResponse>('/api/v1/brokerage/withdrawals', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

export function linkAccount(brokerageName: string, accountId: string): Promise<LinkAccountResponse> {
  return request<LinkAccountResponse>('/api/v1/brokerage/link', {
    method: 'POST',
    body: JSON.stringify({ brokerage_name: brokerageName, account_id: accountId }),
  });
}
